import { useEffect, useState } from 'react';

import type { GetCriticalElementGoalsParams } from 'api/generated/schemas';

import { getCriticalElementGoals } from 'api/generated/critical-element-goals/critical-element-goals';

import { getCurrentFiscalYear } from 'components/partials/insight-modal/shared/halpers';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

interface UseMonitoringGoalsArgs {
  criticalElementId?: string | number;
  relatedGoals?: string[];
}

interface UseMonitoringGoalsResult {
  relatedGoals: string[];
  isLoadingGoals: boolean;
}

export const useMonitoringGoals = ({
  criticalElementId,
  relatedGoals,
}: UseMonitoringGoalsArgs): UseMonitoringGoalsResult => {
  const currentUser = useCurrentUser();
  const [goals, setGoals] = useState<string[]>(relatedGoals || []);
  const [isLoadingGoals, setIsLoadingGoals] = useState(false);

  useEffect(() => {
    const fetchRelated = async () => {
      try {
        setIsLoadingGoals(true);
        const userId = currentUser?.data?.oktaUserId;

        if (!userId || !criticalElementId) return;

        const year = getCurrentFiscalYear();

        const res = await getCriticalElementGoals(
          userId,
          Number(criticalElementId),
          {
            'enteringClassYear.equals': Number(year),
          } as GetCriticalElementGoalsParams,
        );

        const responseData = res as { content?: unknown[] } | unknown[];
        const content =
          (responseData as { content?: unknown[] })?.content ||
          (responseData as unknown[]) ||
          [];
        const names = (content as Array<{ summary?: string }>)
          .map((g) => g?.summary)
          .filter((n): n is string => typeof n === 'string' && n.length > 0);

        if (names.length) setGoals(names);
      } catch (error) {
        console.error('Error fetching related goals', error);
      } finally {
        setIsLoadingGoals(false);
      }
    };

    if (!relatedGoals?.length) {
      fetchRelated();
    }
  }, [criticalElementId, relatedGoals, currentUser?.data?.oktaUserId]);

  return { relatedGoals: goals, isLoadingGoals };
};
